<template>
  <v-form
    v-model="validators.validProductDetails"
    :disabled="viewModeOnly"
    @submit.prevent
  >
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          dense
          outlined
          counter="25"
          maxlength="25"
          :disabled="viewModeOnly"
          :label="
            isServicesTypeProduct
              ? 'Service Name *'
              : 'Brand Name (required unless fresh produce)'
          "
          v-model="product.brand"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          dense
          outlined
          label="Supplier Item Code *"
          counter="20"
          maxlength="20"
          v-model="product.supplierItemId"
          :disabled="
            viewModeOnly ||
            (!newProduct && originalProduct.supplierItemId !== '')
          "
          :rules="[(value) => fieldRequired(value)]"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-text-field
      dense
      outlined
      counter="40"
      maxlength="40"
      label="Product Description *"
      v-model="product.shortDescription"
      :rules="[(value) => fieldRequired(value)]"
    ></v-text-field>

    <div class="align-center mx-0" v-if="!isServicesTypeProduct">
      <p>
        <strong>Sold as</strong>
        <v-icon
          class="i-tooltip-color pl-1 pb-1"
          small
          v-tooltip="'Cannot be edited if product is or has been Live'"
        >
          mdi-help-circle-outline
        </v-icon>
      </p>
      <v-radio-group
        class="i-radio-group"
        v-model="product.soldAs"
        :disabled="!newProduct"
        @change="resetWeightType()"
      >
        <v-row>
          <v-col cols="6" md="4">
            <div
              class="item-div-mini mb-0"
              :class="{ 'item-div-active': product.soldAs === 'FIXED_WEIGHT' }"
            >
              <v-radio label="Fixed Weight" :value="'FIXED_WEIGHT'"></v-radio>
            </div>
          </v-col>
          <v-col cols="6" md="4" v-if="product.productType !== 'LIQUOR'">
            <div
              class="item-div-mini mb-0"
              :class="{
                'item-div-active': product.soldAs === 'VARIABLE_WEIGHT'
              }"
            >
              <v-radio
                label="Variable Weight"
                :value="'VARIABLE_WEIGHT'"
              ></v-radio>
            </div>
          </v-col>
          <v-col cols="6" md="4">
            <div
              class="item-div-mini mb-0"
              :class="{ 'item-div-active': product.soldAs === 'PALLET' }"
            >
              <v-radio label="Pallet" :value="'PALLET'"></v-radio>
            </div>
          </v-col>
        </v-row>
      </v-radio-group>
    </div>

    <v-divider class="mt-n3 mb-6"></v-divider>

    <template v-if="isFixedWeightProduct">
      <v-row class="mt-n4">
        <v-col cols="12" md="4" v-if="!isServicesTypeProduct">
          <i-numeric-field
            dense
            label="Size *"
            :min="0"
            :max="fieldsRulesValues.MAX_UNIT_SIZE"
            outlined
            :precision="2"
            :minPrecision="0"
            :rules="[
              (value) => fieldRequired(value),
              (value) => higherThanZero(value),
              (value) => maxUnitSize(value, fieldsRulesValues.MAX_UNIT_SIZE)
            ]"
            :value="product.unitQty"
            @change="(unitQty) => handleUnitQtyChange(unitQty)"
            @input="(unitQty) => (product.unitQty = unitQty)"
          ></i-numeric-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            attach
            dense
            outlined
            label="Unit *"
            v-model="product.unit"
            :items="mappedUnits"
            :rules="[(value) => fieldRequired(value)]"
          ></v-select>
        </v-col>
        <v-col cols="12" md="4" v-if="showUnitsPerField">
          <i-numeric-field
            dense
            outlined
            :label="unitsPerLabel"
            :min="0"
            :max="999"
            :rules="[
              (value) => fieldRequired(value),
              (value) => higherThanZero(value)
            ]"
            :value="product.packageQuantity"
            @input="(packageQty) => setPackageQtyDebounce(packageQty)"
          ></i-numeric-field>
        </v-col>
      </v-row>

      <v-row class="mt-n4 mb-n2" v-if="!isServicesTypeProduct">
        <template v-if="requiresSameGtin">
          <v-col cols="12">
            <v-alert
              dense
              outlined
              text
              border="left"
              class="mt-n3 mb-n2 i-alert"
              elevation="1"
              type="info"
            >
              Unit & Carton GTIN is the same if sold as a unit
            </v-alert>
          </v-col>
          <v-col cols="12">
            <v-text-field
              dense
              outlined
              :hint="
                baseUnitGtinCheckDigitWarning
                  ? ''
                  : 'GTIN format must be 8, 12, 13 or 14 characters'
              "
              label="Unit & Carton GTIN"
              v-model="product.baseUnitGtin"
              :error="!newProduct && !isValidBaseUnitGtinLength"
              :rules="[
                (value) => numbersOnlyRule(value),
                (value) =>
                  gtinLengthRule(value, fieldsRulesValues.ALLOWED_GTIN_LENGTHS)
              ]"
              @input.capture="normalizeGtinInput"
              @input="(gtin) => handleGtinChange(gtin, true)"
            ></v-text-field>
            <v-alert
              v-if="baseUnitGtinCheckDigitWarning"
              dense
              outlined
              text
              border="left"
              class="mb-2 mt-n5 i-alert"
              elevation="1"
              type="warning"
            >
              Please double check the GTIN as the check digit indicates a
              mistake
            </v-alert>
          </v-col>
        </template>
        <template v-else>
          <v-col cols="12" md="6">
            <v-text-field
              dense
              outlined
              :hint="
                baseUnitGtinCheckDigitWarning
                  ? ''
                  : 'GTIN format must be 8, 12, 13 or 14 characters'
              "
              label="Unit GTIN"
              v-model="product.baseUnitGtin"
              :error="!newProduct && !isValidBaseUnitGtinLength"
              :rules="[
                (value) => numbersOnlyRule(value),
                (value) =>
                  gtinLengthRule(value, fieldsRulesValues.ALLOWED_GTIN_LENGTHS)
              ]"
              @input.capture="normalizeGtinInput"
              @input="(gtin) => handleGtinChange(gtin, true)"
            ></v-text-field>
            <v-alert
              v-if="baseUnitGtinCheckDigitWarning"
              dense
              outlined
              text
              border="left"
              class="mb-2 mt-n5 i-alert"
              elevation="1"
              type="warning"
            >
              Please double check the Unit GTIN as the check digit indicates a
              mistake
            </v-alert>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              dense
              outlined
              :hint="
                gtinCheckDigitWarning
                  ? ''
                  : 'GTIN format must be 8, 12, 13 or 14 characters'
              "
              label="Carton GTIN"
              v-model="product.gtin"
              :error="!newProduct && !isValidGtinLength"
              :rules="[
                (value) => numbersOnlyRule(value),
                (value) =>
                  gtinLengthRule(value, fieldsRulesValues.ALLOWED_GTIN_LENGTHS)
              ]"
              @input.capture="normalizeGtinInput"
              @input="(gtin) => handleGtinChange(gtin, false)"
            ></v-text-field>
            <v-alert
              v-if="gtinCheckDigitWarning"
              dense
              outlined
              text
              border="left"
              class="mb-2 mt-n5 i-alert"
              elevation="1"
              type="warning"
            >
              Please double check the Carton GTIN as the check digit indicates a
              mistake
            </v-alert>
          </v-col>
        </template>
      </v-row>
    </template>

    <template v-else>
      <v-row class="mt-n4 mb-n2">
        <v-col cols="12" md="4">
          <i-numeric-field
            dense
            outlined
            label="Carton Weight (KG) *"
            :min="0"
            :max="9999.99"
            :precision="2"
            :minPrecision="0"
            :rules="[
              (value) => fieldRequired(value),
              (value) => higherThanZero(value)
            ]"
            suffix="KG"
            :value="product.variableWeightTargetInKg"
            @change="(weight) => setVariableWeightTargetInKgDebounce(weight)"
            @input="(weight) => setVariableWeightTargetInKgDebounce(weight)"
          ></i-numeric-field>
        </v-col>
        <v-col cols="12" md="4">
          <i-numeric-field
            dense
            outlined
            :label="`Units Per ${
              product.soldAs === 'PALLET' ? 'Pallet' : 'Carton'
            } *`"
            :min="0"
            :max="999"
            :rules="[
              (value) => fieldRequired(value),
              (value) => higherThanZero(value)
            ]"
            text-align-right
            :value="product.packageQuantity"
            v-model="product.packageQuantity"
            @input="(packageQty) => setPackageQtyDebounce(packageQty)"
          ></i-numeric-field>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            dense
            outlined
            :hint="
              gtinCheckDigitWarning
                ? ''
                : 'GTIN format must be 8, 12, 13 or 14 characters'
            "
            label="Carton GTIN"
            v-model="product.gtin"
            :error="!newProduct && !isValidGtinLength"
            :rules="[
              (value) => numbersOnlyRule(value),
              (value) =>
                gtinLengthRule(value, fieldsRulesValues.ALLOWED_GTIN_LENGTHS)
            ]"
            @input.capture="normalizeGtinInput"
            @input="(gtin) => handleGtinChange(gtin, false)"
          ></v-text-field>
          <v-alert
            v-if="gtinCheckDigitWarning"
            dense
            outlined
            text
            border="left"
            class="mb-2 mt-n5 i-alert"
            elevation="1"
            type="warning"
          >
            Please double check the GTIN as the check digit indicates a mistake
          </v-alert>
        </v-col>
      </v-row>
    </template>

    <template>
      <p class="g-bold mt-2">Select Trading & Category Hierarchy</p>
      <i-search-trading-category-hierarchy
        dense
        class="mt-1 mb-0"
        :viewModeOnly="viewModeOnly"
        :newProduct="newProduct"
        @setProductHierarchy="setProductHierarchy"
        @clearProductHierarchy="resetProductHierarchy"
      />
    </template>

    <template v-if="hasProductHierarchySet">
      <v-row>
        <v-col cols="12">
          <v-text-field
            dense
            disabled
            hide-details
            outlined
            hint="Use Lookup functionality"
            label="Trading Department *"
            v-model="product.categoryProductHierarchy.tradingDepartment"
          ></v-text-field>
        </v-col>

        <v-col cols="6">
          <v-text-field
            dense
            disabled
            hide-details
            outlined
            hint="Use Lookup functionality"
            label="Category *"
            v-model="product.categoryProductHierarchy.category"
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            dense
            disabled
            hide-details
            outlined
            hint="Use Lookup functionality"
            label="Sub-Category *"
            v-model="product.categoryProductHierarchy.subCategory"
          ></v-text-field>
        </v-col>

        <v-col
          v-if="product.categoryProductHierarchy.segment"
          :cols="product.categoryProductHierarchy.subSegment ? 6 : 12"
        >
          <v-text-field
            dense
            disabled
            hide-details
            outlined
            hint="Use Lookup functionality"
            label="Segment *"
            v-model="product.categoryProductHierarchy.segment"
          ></v-text-field>
        </v-col>
        <v-col cols="6" v-if="product.categoryProductHierarchy.subSegment">
          <v-text-field
            dense
            disabled
            hide-details
            outlined
            hint="Use Lookup functionality"
            label="Sub-Segment *"
            v-model="product.categoryProductHierarchy.subSegment"
          ></v-text-field>
        </v-col>
      </v-row>
    </template>

    <v-row
      v-if="
        ownerControlledProductTradingTerms &&
        (product.wasOrIsLive || product.status === 'LIVE_FUTURE')
      "
      dense
    >
      <v-col cols="6" class="font-weight-bold text-h6">
        Trading Terms by Product:
      </v-col>
      <v-col
        cols="6"
        class="font-weight-bold text-h6 grey--text text--darken-3"
      >
        <span class="grey lighten-3 ml-2 px-5 py-3">{{
          $filters.percentage(product.tradingTermsPercentage)
        }}</span>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import FieldValidators from '@/utils/FieldValidators'
import { normalizeGtin } from '@/utils/gtin'
import iNumericField from '@/components/NumericField'
import iSearchTradingCategoryHierarchy from '../products/SearchTradingCategoryHierarchy'
import Bugsnag from '@bugsnag/js'
import { mapGetters } from 'vuex'
import { formatNumberForLocale } from '@/utils/numberFormatting'

export default {
  components: {
    iSearchTradingCategoryHierarchy,
    iNumericField
  },

  props: {
    newProduct: {
      type: Boolean,
      default: true
    },
    product: {
      type: Object,
      default: () => {}
    },
    originalProduct: {
      type: Object,
      default: () => {}
    },
    validators: {
      type: Object,
      default: () => {}
    },
    fieldsRulesValues: {
      type: Object,
      default: () => {}
    },
    isFixedWeightProduct: {
      type: Boolean,
      default: true
    },
    requiresSameGtin: {
      type: Boolean,
      default: false
    },
    units: {
      type: Array,
      default: () => []
    },
    viewModeOnly: {
      type: Boolean,
      default: true
    },

    isServicesTypeProduct: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    debouncePackageQuantity: null,
    debounceVariableWeightTargetInKg: null,
    ...FieldValidators,
    initialCategoryProductHierarchy: null,
    baseUnitGtinCheckDigitWarning: false,
    gtinCheckDigitWarning: false
  }),

  computed: {
    ...mapGetters('tenant', [
      'ownerControlledProductTradingTerms',
      'tenantLocale'
    ]),

    mappedUnits() {
      return this.units.map((unit) => {
        return {
          text: unit.displayName,
          value: unit.unit
        }
      })
    },

    hasProductHierarchySet() {
      const productHierarchy = this.product.categoryProductHierarchy
      return (
        productHierarchy?.channel &&
        productHierarchy?.tradingDepartment &&
        productHierarchy?.category &&
        productHierarchy?.subCategory
      )
    },

    // ***
    // Created to alert fields that cannot be validated when uploaded via Bulk Uploads
    // ***

    isValidBaseUnitGtinLength() {
      return this.fieldsRulesValues.ALLOWED_GTIN_LENGTHS.includes(
        this.product.baseUnitGtin.length
      )
    },

    isValidGtinLength() {
      return this.fieldsRulesValues.ALLOWED_GTIN_LENGTHS.includes(
        this.product.gtin.length
      )
    },

    unitsPerLabel() {
      if (this.isServicesTypeProduct) {
        const u = (this.product.unit || '').toUpperCase()
        if (u === 'HR') return 'Units Quantity*'
        if (u === 'D') return 'Units Quantity *'
        if (u === 'M2') return 'Units Quantity'
        return 'Units *'
      }

      return `Units Per ${
        this.product.soldAs === 'PALLET' ? 'Pallet' : 'Carton'
      } *`
    },

    showUnitsPerField() {
      if (!this.isServicesTypeProduct) return true

      const unit = (this.product.unit || '').toUpperCase()

      return unit !== 'EA'
    }
  },

  methods: {
    hasValidGtinCheckDigit(gtin) {
      try {
        if (this.fieldsRulesValues.ALLOWED_GTIN_LENGTHS.includes(gtin.length)) {
          const gtinWithoutCheckDigit = `${gtin}`.slice(0, -1)
          const existingCheckDigit = Number(gtin.at(-1))
          const calculatedCheckDigit = this.calculateGtinCheckDigit(
            gtinWithoutCheckDigit
          )
          return existingCheckDigit === calculatedCheckDigit
        } else {
          return true // if it is not the correct length there is no point in validating the check digit
        }
      } catch (err) {
        Bugsnag.notify(err)
        return true // it's only a warning anyway in the worst case so if anything goes wrong during the calculation just ignore it
      }
    },

    normalizeGtinInput(event) {
      // Clean the native value before the text field validates or updates its model.
      if (event.target?.value != null)
        event.target.value = normalizeGtin(event.target.value)
    },

    handleGtinChange(gtin, isBaseUnit) {
      const normalizedGtin = normalizeGtin(gtin)
      this.product[isBaseUnit ? 'baseUnitGtin' : 'gtin'] = normalizedGtin
      if (!normalizedGtin || this.hasValidGtinCheckDigit(normalizedGtin)) {
        if (isBaseUnit) {
          this.baseUnitGtinCheckDigitWarning = false
        } else {
          this.gtinCheckDigitWarning = false
        }
      } else {
        if (isBaseUnit) {
          this.baseUnitGtinCheckDigitWarning = true
        } else {
          this.gtinCheckDigitWarning = true
        }
      }
    },

    handleUnitQtyChange(unitQty) {
      // Only show decimals if original number had decimals
      this.product.unitQty =
        +unitQty === Math.floor(unitQty)
          ? unitQty
          : this.maxNumberDecimals(unitQty, 2, 0)
    },

    maxNumberDecimals(value, maxDecimals, minDecimals = maxDecimals) {
      if (value < 0) return 0
      const formatOptions = {
        useGrouping: false,
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals
      }
      return formatNumberForLocale(value, formatOptions, this.tenantLocale)
    },

    resetWeightType() {
      this.product.hasGst = this.isFixedWeightProduct
      this.product.hasWet = false
      this.product.unitQty =
        this.product.packageQuantity =
        this.product.variableWeightTargetInKg =
          ''
      this.product.unit = this.isFixedWeightProduct ? '' : 'KG'
      this.product.regionalPrices = []
    },

    setVariableWeightTargetInKgDebounce(variableWeightTargetInKg) {
      const DEBOUNCE_DELAY = 750
      this.product.variableWeightTargetInKg = this.maxNumberDecimals(
        variableWeightTargetInKg,
        2,
        0
      )

      clearTimeout(this.debounceVariableWeightTargetInKg)
      this.debounceVariableWeightTargetInKg = setTimeout(() => {
        this.$emit('recalculate-all-region-gpp')
      }, DEBOUNCE_DELAY)
    },

    setPackageQtyDebounce(packageQuantity) {
      const DEBOUNCE_DELAY = 750
      this.product.packageQuantity = this.maxNumberDecimals(packageQuantity, 0)

      if (this.product.packageQuantity > 0) {
        clearTimeout(this.debouncePackageQuantity)
        this.debouncePackageQuantity = setTimeout(() => {
          this.$emit('recalculate-all-region-gpp')
        }, DEBOUNCE_DELAY)
      }
    },

    setProductHierarchy(hierarchyData) {
      this.product.categoryProductHierarchy = {
        ...this.product.categoryProductHierarchy,
        subSegment: hierarchyData.subSegment,
        segment: hierarchyData.segment,
        category: hierarchyData.category,
        subCategory: hierarchyData.subCategory,
        tradingDepartment: hierarchyData.tradingDepartment
      }
    },

    resetProductHierarchy() {
      this.product.categoryProductHierarchy =
        this.initialCategoryProductHierarchy
    },

    calculateGtinCheckDigit(gtin) {
      const gtinArray = `${gtin}`.split('').reverse()

      let total = 0
      let i = 1
      gtinArray.forEach((number) => {
        number = parseInt(number)
        if (i % 2 === 0) {
          total = total + number
        } else {
          total = total + number * 3
        }
        i++
      })

      return Math.ceil(total / 10) * 10 - total
    }
  },

  mounted() {
    this.initialCategoryProductHierarchy =
      this.product?.categoryProductHierarchy
    if (this.product && !this.newProduct) {
      // we want to display potential gtin check digit issues even for existing products so we call the handling methods explicitly for those
      this.handleGtinChange(this.product.gtin, false)
      this.handleGtinChange(this.product.baseUnitGtin, true)
    }
  }
}
</script>
